import type { VoyageReportKind } from './schemas.js';

/**
 * Raporların yakıt satırlarının tek okuyucusu (ADR 0090 §1). Özet (`voyageSummary`), CP penceresi
 * (`voyagePerformance`), iyi hava günleri (`cpDays`), yakıt analizi (`fuelAnalysis`) ve formun
 * "önceki ROB"u aynı satırları okur; formüller burada bir kez yazılır. Saf işlevler.
 *
 * Satırların yeri (ADR 0057 §3): Noon'da `data.fuel.fuels` (açılış, tüketim, ikmal; kapanış
 * hesaplanır), diğer raporlarda ROB tablosu `data.rob` (kalan, son rapordan beri tüketim).
 */

/** Yakıt satırı okunacak rapor: tür ve veri yeter. */
export interface FuelReport {
  kind: VoyageReportKind;
  data: Readonly<Record<string, unknown>>;
}

const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};
const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;
const number = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Yakıt ve yağ: Closing = Opening − Consumption + Bunkering. */
export function closingRob(row: {
  opening: number;
  consumption: number;
  bunkered: number;
}): number {
  return round(row.opening - row.consumption + row.bunkered);
}

/** Raporun yakıt satırları: Noon'da yakıt tablosu, diğerlerinde ROB tablosu. */
function rowsOf(report: FuelReport): Record<string, unknown>[] {
  const rows = report.kind === 'noon' ? record(report.data.fuel).fuels : report.data.rob;
  return Array.isArray(rows) ? rows.map(record) : [];
}

/**
 * Raporun "son rapordan beri" tüketimi, yakıt türüne göre: Noon'da `consumption`, diğerlerinde
 * ROB tablosunun `consumptionMt`'si. Tüketimi yazılmamış satır gelmez.
 */
export function reportConsumption(report: FuelReport): [string, number][] {
  const key = report.kind === 'noon' ? 'consumption' : 'consumptionMt';
  return rowsOf(report).flatMap((row) => {
    const fuel = text(row.fuel);
    const value = number(row[key]);
    return fuel && value !== null ? [[fuel, value] as [string, number]] : [];
  });
}

/**
 * Raporun yakıt ROB'ları: Noon'da kapanış (`closingRob`; ikmal yoksa 0), diğerlerinde ROB
 * tablosunun kalanı. Açılışı ya da tüketimi olmayan Noon satırı, kalanı olmayan ROB satırı gelmez.
 */
export function reportRob(report: FuelReport): Map<string, number> {
  const fuels = new Map<string, number>();
  for (const row of rowsOf(report)) {
    const fuel = text(row.fuel);
    if (!fuel) continue;
    if (report.kind === 'noon') {
      const opening = number(row.opening);
      const consumption = number(row.consumption);
      if (opening === null || consumption === null) continue;
      fuels.set(fuel, closingRob({ opening, consumption, bunkered: number(row.bunkered) ?? 0 }));
    } else {
      const rob = number(row.rob);
      if (rob !== null) fuels.set(fuel, rob);
    }
  }
  return fuels;
}

/**
 * Raporda yazılan ikmal (MT), yakıt türüne göre: Noon'un yakıt tablosunda `bunkered`; diğer
 * raporlarda ROB satırının `bunkeredMt`'si. Bu alanı defteri açık gemide anlık görüntü yazar
 * (aralıktaki onaylı alım; ADR 0090 Ek, ADR 0117 §2.4); elle girilen ROB tablosunda yoktur ve
 * ikmal ROB artışından anlaşılır (ADR 0090 §3).
 */
export function reportBunkered(report: FuelReport): Map<string, number> {
  const fuels = new Map<string, number>();
  const key = report.kind === 'noon' ? 'bunkered' : 'bunkeredMt';
  for (const row of rowsOf(report)) {
    const fuel = text(row.fuel);
    const value = number(row[key]);
    if (fuel && value !== null && value > 0) fuels.set(fuel, (fuels.get(fuel) ?? 0) + value);
  }
  return fuels;
}
